import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, CreditCard, Sparkles, Zap, ExternalLink, RefreshCw, AlertTriangle } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { shouldUseAppleIAP, getAppleProductIdForTier, APPLE_SUBSCRIPTION_PRODUCTS } from '../services/platform.service';
import { purchaseSubscription, restorePurchases } from '../services/apple-iap.service';
import { Browser } from '@capacitor/browser';

const API_URL = import.meta.env.VITE_API_URL || '';

interface SubscriptionPlan {
  id: string;
  tier: string;
  name: string;
  monthlyCredits: number;
  price: number;
  features: string[];
  isPopular: boolean;
  isBestValue: boolean;
}

interface CurrentSubscription {
  subscription: {
    tier: string;
    status: string;
    monthlyCredits: number;
    creditsRemaining: number;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    canceledAt: string | null;
    stripeSubscriptionId: string | null;
  };
  balance: {
    creditsRemaining: number;
    monthlyCredits: number;
    tier: string;
    percentage: number;
    isLowCredits: boolean;
    nextResetDate: string | null;
  };
}

interface InvoiceItem {
  id: string;
  number: string | null;
  status: string;
  amount: number;
  currency: string;
  date: string;
  pdfUrl: string | null;
  hostedUrl: string | null;
}

interface PaymentMethodItem {
  id: string;
  brand: string | undefined;
  last4: string | undefined;
  expMonth: number | undefined;
  expYear: number | undefined;
}

// RON price mapping
const ronPrices: Record<string, string> = {
  'FREE': '',
  'BASIC': '29.99',
  'PREMIUM': '49.99',
  'UNLIMITED': '99.99',
};

export default function SubscriptionsPage() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [current, setCurrent] = useState<CurrentSubscription | null>(null);
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionTier, setActionTier] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [plansRes, currentRes, invoicesRes, methodsRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/plans`),
        axios.get(`${API_URL}/api/subscriptions/current`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/subscriptions/invoices`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/subscriptions/payment-methods`, { headers: getAuthHeaders() }),
      ]);

      setPlans(plansRes.data.plans || []);
      setCurrent(currentRes.data);
      setInvoices(invoicesRes.data.invoices || []);
      setPaymentMethods(methodsRes.data.paymentMethods || []);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const planStyles = (tier: string) => {
    switch (tier) {
      case 'FREE':
        return {
          badge: 'bg-gray-100 text-gray-700',
          card: 'border-gray-200',
          topBadge: 'bg-green-500',
          topBadgeText: '✓ ACTIV',
          creditsBg: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200',
          creditsText: 'text-green-700',
          creditsNumber: 'text-green-600',
          btn: 'bg-green-100 text-green-700',
        };
      case 'BASIC':
        return {
          badge: 'bg-blue-100 text-blue-700',
          card: 'border-blue-200',
          topBadge: 'bg-blue-600',
          topBadgeText: '⭐ POPULAR',
          creditsBg: 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200',
          creditsText: 'text-blue-700',
          creditsNumber: 'text-blue-600',
          btn: 'bg-blue-600 text-white hover:bg-blue-700',
        };
      case 'PREMIUM':
        return {
          badge: 'bg-purple-100 text-purple-700',
          card: 'border-purple-300',
          topBadge: 'bg-gradient-to-r from-orange-500 to-yellow-500',
          topBadgeText: '⚡ RECOMANDAT',
          creditsBg: 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200',
          creditsText: 'text-purple-700',
          creditsNumber: 'text-purple-600',
          btn: 'bg-blue-600 text-white hover:bg-blue-700',
        };
      case 'UNLIMITED':
        return {
          badge: 'bg-yellow-100 text-yellow-700',
          card: 'border-yellow-300',
          topBadge: 'bg-gradient-to-r from-amber-500 to-yellow-500',
          topBadgeText: '👑 BEST VALUE',
          creditsBg: 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200',
          creditsText: 'text-amber-700',
          creditsNumber: 'text-amber-600',
          btn: 'bg-blue-600 text-white hover:bg-blue-700',
        };
      default:
        return {
          badge: 'bg-gray-100 text-gray-700',
          card: 'border-gray-200',
          topBadge: 'bg-gray-500',
          topBadgeText: '',
          creditsBg: 'bg-gray-50 border-gray-200',
          creditsText: 'text-gray-700',
          creditsNumber: 'text-gray-600',
          btn: 'bg-gray-600 text-white hover:bg-gray-700',
        };
    }
  };

  const statusLabel = (status?: string) => {
    switch (status) {
      case 'active':
        return 'Activ';
      case 'past_due':
        return 'Plată eșuată';
      case 'canceled':
        return 'Anulat';
      case 'incomplete':
        return 'Incomplet';
      case 'trialing':
        return 'Trial';
      default:
        return status || 'Necunoscut';
    }
  };

  const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString('ro-RO') : '-');

  const tierDisplayName = (tier: string) => {
    switch (tier) {
      case 'FREE': return 'Gratuit';
      case 'BASIC': return 'Basic';
      case 'PREMIUM': return 'Premium';
      case 'UNLIMITED': return 'Gold';
      default: return tier;
    }
  };

  const hasStripeSubscription = useMemo(() => {
    return Boolean(current?.subscription.stripeSubscriptionId);
  }, [current]);

  const isAppleIAP = shouldUseAppleIAP();

  const handlePlanAction = async (tier: string) => {
    if (!current || tier === current.subscription.tier) return;

    setActionTier(tier);

    try {
      // ===== iOS: Apple In-App Purchase =====
      if (isAppleIAP) {
        setActionBusy('checkout');
        const appleProductId = getAppleProductIdForTier(tier);
        if (!appleProductId) {
          alert('Acest plan nu este disponibil pe iOS.');
          return;
        }

        const result = await purchaseSubscription(appleProductId);
        if (result.success) {
          alert(`Abonament ${tier} activat cu succes! +${result.credits || 0} credite.`);
          await loadAll();
        } else {
          alert(result.error || 'Eroare la achiziția prin Apple.');
        }
        return;
      }

      // ===== Android/Web: Stripe =====
      if (hasStripeSubscription) {
        setActionBusy('change');
        await axios.post(
          `${API_URL}/api/subscriptions/change-plan`,
          { tier },
          { headers: getAuthHeaders() }
        );
        alert(`Planul a fost schimbat la ${tier}.`);
        await loadAll();
      } else {
        // New subscription
        setActionBusy('checkout');
        const res = await axios.post(
          `${API_URL}/api/subscriptions/upgrade`,
          { tier },
          { headers: getAuthHeaders() }
        );
        window.location.href = res.data.url;
      }
    } catch (error: any) {
      console.error('Error changing plan:', error);
      alert(error.response?.data?.error || 'Eroare la schimbarea planului');
    } finally {
      setActionTier(null);
      setActionBusy(null);
    }
  };

  const handleRestorePurchases = async () => {
    try {
      setSyncing(true);
      const result = await restorePurchases();
      if (result.success) {
        alert(result.message || 'Achiziții restaurate cu succes!');
        await loadAll();
      } else {
        alert(result.error || 'Nu s-au putut restaura achizițiile.');
      }
    } catch (error: any) {
      alert('Eroare la restaurarea achizițiilor.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await axios.post(
        `${API_URL}/api/subscriptions/sync`,
        {},
        { headers: getAuthHeaders() }
      );
      await loadAll();
      alert('Abonamentul și creditele au fost sincronizate.');
    } catch (error: any) {
      console.error('Error syncing subscription:', error);
      alert(error.response?.data?.error || 'Eroare la sincronizare');
    } finally {
      setSyncing(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Ești sigur că vrei să anulezi abonamentul? Vei păstra accesul până la finalul perioadei.')) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/subscriptions/cancel`, {}, { headers: getAuthHeaders() });
      alert('Abonament anulat. Vei păstra accesul până la finalul perioadei.');
      await loadAll();
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      alert(error.response?.data?.error || 'Eroare la anularea abonamentului');
    }
  };

  const handleCancelImmediately = async () => {
    if (!confirm('Anulezi imediat abonamentul? Vei pierde accesul la finalul minutei.')) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/subscriptions/cancel-immediately`, {}, { headers: getAuthHeaders() });
      alert('Abonamentul a fost anulat imediat.');
      await loadAll();
    } catch (error: any) {
      console.error('Error canceling immediately:', error);
      alert(error.response?.data?.error || 'Eroare la anularea imediată');
    }
  };

  const handleReactivate = async () => {
    try {
      await axios.post(`${API_URL}/api/subscriptions/reactivate`, {}, { headers: getAuthHeaders() });
      alert('Abonament reactivat cu succes!');
      await loadAll();
    } catch (error: any) {
      console.error('Error reactivating subscription:', error);
      alert(error.response?.data?.error || 'Eroare la reactivare');
    }
  };

  const openBillingPortal = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/subscriptions/billing-portal`,
        {},
        { headers: getAuthHeaders() }
      );
      window.location.href = res.data.url;
    } catch (error: any) {
      console.error('Error opening billing portal:', error);
      alert(error.response?.data?.error || 'Nu pot deschide portalul de facturare');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Abonamente" subtitle="Încarcă..." />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Abonamente & Credite FII FIT"
        subtitle="Gestionează abonamentul și creditele tale"
        backTo="/dashboard"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        {/* Current Subscription Card */}
        {current && (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl shadow-xl p-6 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="w-6 h-6" />
                  <h2 className="text-2xl font-bold">Planul Tău Curent</h2>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-white/20 backdrop-blur">
                    {tierDisplayName(current.subscription.tier)}
                  </span>
                </div>
                <p className="text-blue-100">
                  Status: <strong>{statusLabel(current.subscription.status)}</strong>
                </p>
                {current.subscription.cancelAtPeriodEnd && (
                  <p className="text-yellow-100 mt-2">
                    ⚠️ Anulare programată la {formatDate(current.subscription.currentPeriodEnd)}
                  </p>
                )}
              </div>

              <div className="md:text-right">
                <div className="text-5xl font-bold">
                  {Number.isInteger(current.balance.creditsRemaining) ? current.balance.creditsRemaining : parseFloat(current.balance.creditsRemaining.toFixed(2))}
                </div>
                <div className="text-blue-100">
                  din {current.balance.monthlyCredits} credite disponibile
                </div>
                <div className="mt-3 w-full md:w-64 bg-white/20 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      current.balance.isLowCredits ? 'bg-red-300' : 'bg-green-300'
                    }`}
                    style={{ width: `${Math.min(current.balance.percentage, 100)}%` }}
                  />
                </div>

                {current.balance.nextResetDate && (
                  <div className="text-xs text-blue-100 mt-2">
                    Reset lunar: {formatDate(current.balance.nextResetDate)}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <button
                    onClick={() => navigate('/extra-credits')}
                    className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition font-medium text-sm flex items-center justify-center gap-2"
                  >
                    <Zap className="w-4 h-4" />
                    Cumpără Credite Extra
                  </button>
                  {isAppleIAP ? (
                    <button
                      onClick={handleRestorePurchases}
                      className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition text-sm flex items-center justify-center gap-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                      {syncing ? 'Se restaurează...' : 'Restaurează achizițiile'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleSync}
                        className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition text-sm flex items-center justify-center gap-2"
                      >
                        <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Se sincronizează...' : 'Sincronizează'}
                      </button>
                      <button
                        onClick={openBillingPortal}
                        className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition text-sm flex items-center justify-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        Gestionare Facturare
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-3">
              {isAppleIAP ? (
                current.subscription.tier !== 'FREE' && (
                  <button
                    onClick={() => Browser.open({ url: 'https://apps.apple.com/account/subscriptions' })}
                    className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition text-sm flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Gestionează abonamentul (Apple)
                  </button>
                )
              ) : (
                <>
              {!current.subscription.cancelAtPeriodEnd && current.subscription.tier !== 'FREE' && (
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 bg-red-500/20 text-white rounded-lg hover:bg-red-500/30 transition text-sm"
                >
                  Anulează Abonament
                </button>
              )}
              {current.subscription.cancelAtPeriodEnd && (
                <button
                  onClick={handleReactivate}
                  className="px-4 py-2 bg-green-500/30 text-white rounded-lg hover:bg-green-500/40 transition text-sm"
                >
                  Reactivează
                </button>
              )}
              {current.subscription.tier !== 'FREE' && (
                <button
                  onClick={handleCancelImmediately}
                  className="px-4 py-2 bg-red-600/30 text-white rounded-lg hover:bg-red-600/40 transition text-sm flex items-center gap-2"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Anulează Imediat
                </button>
              )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrent = current?.subscription.tier === plan.tier;
            const style = planStyles(plan.tier);

            // On iOS, override price and credits with Apple values
            const appleProduct = isAppleIAP
              ? APPLE_SUBSCRIPTION_PRODUCTS.find(p => p.tier === plan.tier)
              : null;
            const displayPrice = appleProduct ? appleProduct.price : plan.price;
            const displayCredits = appleProduct ? appleProduct.monthlyCredits : plan.monthlyCredits;
            const ronPrice = ronPrices[plan.tier] || '';

            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-lg overflow-visible transition-all hover:shadow-xl flex flex-col border-2 ${
                  isCurrent ? 'ring-2 ring-green-500' : style.card
                }`}
              >
                {/* Top Badge - outside card */}
                {(isCurrent || plan.isPopular || plan.tier === 'PREMIUM' || plan.isBestValue) && (
                  <div className="absolute -top-4 left-4 z-10">
                    <span className={`inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-md ${
                      isCurrent ? 'bg-green-500' : style.topBadge
                    }`}>
                      {isCurrent ? '✓ ACTIV' : style.topBadgeText}
                    </span>
                  </div>
                )}

                <div className="p-6 pt-8 flex flex-col flex-1">
                  {/* Tier Badge */}
                  <div className={`inline-block px-3 py-1 rounded-md text-xs font-semibold mb-3 w-fit border ${style.badge}`}>
                    {plan.tier === 'UNLIMITED' ? 'GOLD' : plan.tier}
                  </div>

                  {/* Plan Name */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>

                  {/* Price */}
                  <div className="mb-5">
                    {plan.price === 0 ? (
                      <div>
                        <span className="text-4xl font-extrabold text-gray-900">Gratuit</span>
                        <span className="text-gray-500 ml-2 text-sm">fără abonament</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-4xl font-extrabold text-gray-900">
                          €{displayPrice.toFixed(2)}
                        </span>
                        <div className="text-gray-500 text-sm">
                          /lună {ronPrice ? `· ${ronPrice} lei` : ''}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Credits Box */}
                  <div className={`mb-6 px-4 py-4 rounded-xl border ${style.creditsBg}`}>
                    <div className={`text-4xl font-extrabold ${style.creditsNumber}`}>{displayCredits}</div>
                    <div className={`text-sm font-bold ${style.creditsText}`}>credite FII FIT</div>
                    <div className={`text-xs ${style.creditsText} opacity-75 mt-0.5`}>
                      {plan.tier === 'FREE' ? 'credite FII FIT acordate o singură dată' : 'credite FII FIT pe lună'}
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6 flex-1">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Bottom section */}
                  <div className="mt-auto">
                    {/* Subscription type note */}
                    <div className="mb-3 px-3 py-2 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">
                        {plan.price > 0 ? 'Abonament lunar' : 'Nu se reînnoiește lunar'}
                      </p>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => handlePlanAction(plan.tier)}
                      disabled={isCurrent || actionTier === plan.tier}
                      className={`w-full py-3.5 rounded-xl font-bold transition-all text-base ${
                        isCurrent
                          ? 'bg-green-100 text-green-700 cursor-not-allowed'
                          : actionTier === plan.tier
                          ? 'bg-gray-300 text-gray-600 cursor-wait'
                          : style.btn
                      }`}
                    >
                      {actionTier === plan.tier ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          {actionBusy === 'checkout' ? 'Plată...' : 'Actualizare...'}
                        </span>
                      ) : isCurrent ? (
                        'Plan de test activ'
                      ) : plan.tier === 'FREE' ? (
                        'Plan Gratuit'
                      ) : (
                        hasStripeSubscription ? `Schimbă la ${plan.name}` : `Alege ${plan.name}`
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment Methods - only show on Stripe (Android/Web) */}
        {!isAppleIAP && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Metode de Plată</h3>
              <button
                onClick={openBillingPortal}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-2"
              >
                Administrează <ExternalLink className="w-4 h-4" />
              </button>
            </div>

            {paymentMethods.length === 0 ? (
              <p className="text-sm text-gray-600">Nu există metode de plată salvate.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {paymentMethods.map((pm) => (
                  <div key={pm.id} className="border rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-900">
                        {pm.brand?.toUpperCase()} •••• {pm.last4}
                      </div>
                      <div className="text-sm text-gray-500">
                        Expiră: {pm.expMonth}/{pm.expYear}
                      </div>
                    </div>
                    <CreditCard className="w-6 h-6 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscription Disclaimer + Required Links */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-blue-200">
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-blue-700 hover:text-blue-900 underline">
              📄 Termeni și Condiții (EULA)
            </a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-blue-700 hover:text-blue-900 underline">
              🔒 Politica de Confidențialitate
            </a>
          </div>
          {isAppleIAP ? (
            <>
              <p className="text-xs text-blue-800 leading-relaxed mb-2">
                <strong>ℹ️ Informații despre abonament:</strong> Plata se procesează securizat prin Apple și se debitează din contul tău Apple ID la confirmarea achiziției. Abonamentul se reînnoiește automat, la același preț, dacă nu este anulat cu cel puțin 24 de ore înainte de finalul perioadei. Poți gestiona sau anula abonamentul din Setări → Apple ID → Abonamente pe dispozitivul tău iOS.
              </p>
              <p className="text-xs text-blue-700 leading-relaxed">
                Creditele AI neutilizate nu se transferă în luna următoare. Pentru asistență, contactați <a href="mailto:office@fiifit.ro" className="underline font-medium">office@fiifit.ro</a>.
              </p>
            </>
          ) : (
            <>
              <p className="text-xs text-blue-800 leading-relaxed mb-2">
                <strong>ℹ️ Informații despre abonament:</strong> Plata se procesează securizat prin Stripe. Abonamentul se reînnoiește automat la finalul perioadei selectate. Poți anula oricând din această pagină sau din portalul de facturare — vei păstra accesul până la finalul perioadei plătite.
              </p>
              <p className="text-xs text-blue-700 leading-relaxed">
                Creditele AI neutilizate nu se transferă în luna următoare. Prețurile afișate includ TVA. Pentru rambursări, contactați <a href="mailto:office@fiifit.ro" className="underline font-medium">office@fiifit.ro</a> în termen de 14 zile de la achiziție.
              </p>
            </>
          )}
        </div>

        {/* Invoices - only show on Stripe (Android/Web) */}
        {!isAppleIAP && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Facturi</h3>
            <button
              onClick={loadAll}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Reîncarcă
            </button>
          </div>

          {invoices.length === 0 ? (
            <p className="text-sm text-gray-600">Nu există facturi încă.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-gray-500">
                  <tr>
                    <th className="py-2">Număr</th>
                    <th>Data</th>
                    <th>Status</th>
                    <th>Sumă</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-t">
                      <td className="py-2 font-medium text-gray-900">
                        {inv.number || inv.id.slice(0, 8)}
                      </td>
                      <td>{formatDate(inv.date)}</td>
                      <td className="capitalize">{inv.status}</td>
                      <td>
                        {inv.amount.toFixed(2)} {inv.currency.toUpperCase()}
                      </td>
                      <td className="text-right">
                        {inv.hostedUrl && (
                          <a
                            className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                            href={inv.hostedUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Vezi <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
